import React, { useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useFormik } from "formik";
import { format, addDays, eachDayOfInterval, parseISO } from "date-fns";

import useCartTotal from "../../../Common/Hooks/useCartTotal";
import useTax from "../../../Common/Hooks/useTax";
import {
  fetchTimeSlot,
  isDateBetween,
  walletRetrieve,
} from "../../../Services/MainService";
import {
  calculateBundleOffer,
  createOrder,
  fetchLaundryItem,
  fetchServices,
} from "../../../Services/CartService";
import { fetchCoupon, fetchWeekDaysOff } from "../../../Services/OrderService";
import { baseUrl } from "../../../Services/Axios";
import { getItem } from "../../../Services/Storage";
import "../../../Store/Pages/Orders/NewOrder.css";

import StepCard from "./StepCard";
import OrderStepper from "./OrderStepper";
import ItemsStep from "./ItemsStep";
import SummaryStep from "./SummaryStep";
import ReviewStep from "./ReviewStep";
import CartSidebar from "./CartSidebar";
import OrderModals from "./Modals";

export const ConsumerNewOrder = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const config = useSelector((x) => x.Config);
  const deliveryTypes = useSelector((x) => x.OrderTypes);
  const orderTypeId = useSelector((x) => x.OrderTypeId);
  const bookingType = useSelector((x) => x.BookingType);
  const cart = useSelector((x) => x.Cart);
  const lastService = useSelector((x) => x.Service);
  const lastCategory = useSelector((x) => x.Category);
  const customer = useSelector((x) => x.Customer);
  const loggedIn = useSelector((x) => x.LoggedIn);
  const zone = useSelector((x) => x.Zone);
  const pickupDate = useSelector((x) => x.PickupDate);
  const deliveryDate = useSelector((x) => x.DeliveryDate);
  const addresses = useSelector((x) => x.Addresses);
  const selectedAddress = useSelector((x) => x.Address);
  const coupon = useSelector((x) => x.Coupon);
  const note = useSelector((x) => x.Note);
  const ongoingOrders = useSelector((x) => x.OngoingOrders);
  const apiNotes = useSelector((x) => x.Customer?.customer_note) || [];

  // --- Step state ---
  const [step, setStep] = React.useState(1);

  // --- Items (Step 1) state ---
  const [services, setServices] = React.useState([]);
  const [categories, setCategories] = React.useState([]);
  const [initLoading, setInitLoading] = React.useState(true);
  const [itemsLoading, setItemsLoading] = React.useState(false);
  const [laundryItems, setLaundryItem] = React.useState([]);
  const [campaign, setCampaign] = React.useState(null);
  const [payload, setPayload] = React.useState({
    serviceId: null,
    categoryId: null,
    limit: 100,
    offset: 1,
  });
  const [cShow, setCShow] = React.useState(false);
  const [reload, setReload] = React.useState("");
  const [tempServ, setTempServ] = React.useState(null);
  const [tempBook, setTempBook] = React.useState(null);
  const [search, setSearch] = React.useState("");
  const [fetchError, setFetchError] = React.useState(false);
  const [open, setOpen] = React.useState({});

  // --- Summary (Step 2) state ---
  const [weekDaysOff, setWeekDaysOff] = React.useState(null);
  const [pickupRange, setPickupRange] = React.useState([]);
  const [deliveryRange, setDeliveryRange] = React.useState([]);
  const [showPickup, setShowPickup] = React.useState(false);
  const [showDelivery, setShowDelivery] = React.useState(false);
  const [sPickup, setSPickup] = React.useState(null);
  const [sPSlot, setSPSlot] = React.useState(null);
  const [sDelivery, setSDelivery] = React.useState(null);
  const [sDSlot, setSDSlot] = React.useState(null);
  const [pickupLoading, setPickupLoading] = React.useState(false);
  const [deliveryLoading, setDeliveryLoading] = React.useState(false);
  const [cLoading, setCLoading] = React.useState(false);
  const [code, setCode] = React.useState(coupon?.coupon?.code ?? "");
  const [addressModal, setAddressModal] = React.useState(null);
  const [address, setAddress] = React.useState({ pickup: null, delivery: null });
  const [loaded, setLoaded] = React.useState(false);
  const [tAddress, setTAddress] = React.useState(null);
  const [showWarning, setShowWarning] = React.useState(false);
  const [items, setItems] = React.useState([]);

  // --- Review (Step 3) state ---
  const [submitting, setSubmitting] = React.useState(false);
  const [paymentMethod, setPaymentMethod] = React.useState("cash_on_delivery");
  const [walletLoading, setWalletLoading] = React.useState(false);
  const [walletBalance, setWalletBalance] = React.useState(0);

  const deliveryType = deliveryTypes?.find((x) => x.id == orderTypeId) || null;
  const [amount, count] = useCartTotal();
  const [subtotal, tax, discount, deliveryCharge, total, grossTotal, referralDiscount] =
    useTax(deliveryType);
  const consumerRefCode = customer?.referral_reward?.consumer_referral_code;

  const formik = useFormik({
    initialValues: { cart: { items: [] } },
  });

  // ==================== ITEMS (Step 1) ====================
  React.useEffect(() => {
    if (!orderTypeId) {
      navigate("/app");
    } else {
      fetchServices(100, 1, orderTypeId).then(
        (res) => {
          const washAndIronIndex = res.findIndex(
            (service) => service?.name?.trim().toLowerCase() === "wash & iron"
          );
          const orderedServices = [...res];
          if (washAndIronIndex > 0) {
            const [washAndIron] = orderedServices.splice(washAndIronIndex, 1);
            orderedServices.unshift(washAndIron);
          }
          setServices(orderedServices);
          let selectedService;
          if (lastService && lastService !== "null") {
            selectedService = orderedServices.find((x) => x.id === parseInt(lastService));
            setPayload((state) => ({ ...state, serviceId: selectedService?.id }));
          } else {
            selectedService = orderedServices[0];
            setPayload((state) => ({ ...state, serviceId: selectedService?.id }));
          }
          const cats = selectedService?.categories || [];
          setCategories(cats);
          let selectedCategory;
          if (lastCategory && lastCategory !== "null") {
            selectedCategory = cats.find((x) => x.id === parseInt(lastCategory));
            setPayload((state) => ({ ...state, categoryId: selectedCategory?.id }));
          } else {
            selectedCategory = cats[0];
            setPayload((state) => ({ ...state, categoryId: selectedCategory?.id }));
          }
          setInitLoading(false);
        },
        (er) => {
          console.warn("Something went wrong on services...");
        }
      );
    }
  }, []);

  React.useEffect(() => {
    const controller = new AbortController();
    if (!initLoading) {
      setItemsLoading(true);
      setLaundryItem([]);
      setFetchError(false);
      fetchLaundryItem(payload, controller.signal).then(
        (res) => {
          if (controller.signal.aborted) return;
          setItemsLoading(false);
          setLaundryItem(res.data);
          setCampaign(res.campain?.[0]);
        },
        (er) => {
          if (controller.signal.aborted) return;
          setItemsLoading(false);
          setLaundryItem([]);
          setFetchError(true);
        }
      );
    }
    return () => controller.abort();
  }, [initLoading, payload, reload]);

  React.useEffect(() => {
    formik.setValues((y) => ({
      ...y,
      cart: {
        ...y.cart,
        items: laundryItems.map((x) => {
          const fCart = cart.find(
            (f) =>
              f.laundry_item_id === x.id && f.serviceId === x.services[0]?.id
          );
          const newPrice =
            bookingType == 2
              ? typeof x.services[0].pivot.e_price == "string"
                ? parseFloat(x.services[0].pivot.e_price)
                : x.services[0].pivot.e_price
              : typeof x.services[0].pivot.price == "string"
              ? parseFloat(x.services[0].pivot.price)
              : x.services[0].pivot.price;
          if (fCart) {
            const fadb9 = {
              ...fCart,
              addons: x.addons.map((z) => {
                const fAd = fCart.addons.find((ffAd) => ffAd.id === z.id);
                if (fAd) return fAd;
                else
                  return {
                    id: z.id,
                    name: z.name,
                    price: z.price,
                    qty: 0,
                  };
              }),
              price: newPrice,
              actualPrice: newPrice,
            };
            if (campaign) {
              fadb9.price = fadb9.price - fadb9.price * (campaign.discount / 100);
            }
            return fadb9;
          } else {
            const fadb9 = {
              laundry_item_id: x.id,
              laundry_item_category_id: parseInt(x.laundry_item_category_id),
              price: newPrice,
              actualPrice: newPrice,
              quantity: 0,
              name: x.name,
              serviceId: x.services[0]?.id,
              is_bundle_active: false,
              bundle_buy: x.bundle_offer?.required_items,
              bundle_free: x.bundle_offer?.total_discount_items,
              free_items: 0,
              addons: x.addons.map((z) => {
                return {
                  id: z.id,
                  name: z.name,
                  price: z.price,
                  qty: 0,
                };
              }),
              item_note: "",
            };
            if (campaign) {
              fadb9.price = fadb9.price - fadb9.price * (campaign.discount / 100);
            }
            return fadb9;
          }
        }),
      },
    }));
  }, [laundryItems, bookingType, campaign]);

  React.useEffect(() => {
    if (!initLoading) {
      const items = [...formik.values.cart.items];
      const currentCart = [...cart];
      const newItems = [...items];

      let updatedCart = currentCart.map((item) => {
        const newItem = newItems.find(
          (newItem) =>
            newItem.laundry_item_id === item.laundry_item_id &&
            newItem.serviceId === item.serviceId
        );
        return newItem || item;
      });

      newItems
        .filter((x) => x.quantity !== 0)
        .forEach((newItem) => {
          if (
            !updatedCart.find(
              (item) =>
                item.laundry_item_id === newItem.laundry_item_id &&
                item.serviceId === newItem.serviceId
            )
          ) {
            updatedCart.push(newItem);
          }
        });

      for (let i = 0; i < updatedCart.length; i++) {
        const x = updatedCart[i];
        const y = { ...x, addons: [] };

        if (x.quantity !== 0) {
          for (let j = 0; j < x.addons.length; j++) {
            const z = x.addons[j];
            if (z.qty !== 0) {
              y.addons.push(z);
            }
          }
        }
        updatedCart[i] = y;
      }
      updatedCart = updatedCart.filter((x) => x.quantity !== 0);
      dispatch({ type: "setCart", payload: updatedCart });
    }
  }, [formik.values, initLoading, dispatch]);

  const selectService = (serviceId, clearTemp) => {
    setSearch("");
    if (
      orderTypeId != 1 &&
      orderTypeId != 2 &&
      serviceId != lastService &&
      count != 0 &&
      !clearTemp
    ) {
      setTempServ(serviceId);
    } else {
      setTempServ(null);
      const serv = services.find((x) => x.id == serviceId);
      const cats = serv?.categories || [];
      setCategories(cats);
      setPayload((state) => ({ ...state, serviceId, categoryId: cats[0]?.id }));
      dispatch({ type: "setService", payload: serviceId });
    }
  };
  const selectCategory = (categoryId) => {
    setSearch("");
    setPayload((state) => ({ ...state, categoryId }));
    dispatch({ type: "setCategory", payload: categoryId });
  };
  const toggleAddons = (index) => {
    setLaundryItem((prevItems) =>
      prevItems.map((item, i) =>
        i === index ? { ...item, showAddons: !item.showAddons } : item
      )
    );
  };
  const changeItemQuantity = (index, value) => {
    formik.setValues((v) => ({
      cart: {
        items: v.cart.items.map((v, i) => {
          if (i === index) {
            const item = { ...v, quantity: value };
            if (item.bundle_buy > 0)
              item.free_items = calculateBundleOffer({
                totalQuantity: value,
                bundleBuy: item.bundle_buy,
                bundleFree: item.bundle_free,
                itemPrice: item.price,
              }).freeItems;

            item.is_bundle_active = item.free_items > 0;

            return item;
          }
          return v;
        }),
      },
    }));
  };
  const changeAddonQuantity = (itemIndex, addonIndex, qty) => {
    formik.setValues((v) => ({
      cart: {
        items: v.cart.items.map((v, i) =>
          i === itemIndex
            ? {
                ...v,
                addons: v.addons.map((ad, adI) =>
                  addonIndex === adI ? { ...ad, qty } : ad
                ),
              }
            : v
        ),
      },
    }));
  };
  const changeItemNote = (itemIndex, item_note) => {
    formik.setValues((v) => ({
      cart: {
        items: v.cart.items.map((item, i) =>
          i === itemIndex ? { ...item, item_note } : item
        ),
      },
    }));
  };
  const cCart = (serv, isServ = true) => {
    dispatch({ type: "setCart", payload: [] });
    formik.setValues({ cart: { items: [] } });
    if (isServ && serv) {
      setTimeout(() => {
        selectService(serv, true);
      }, 100);
    } else if (!isServ && serv) {
      setTimeout(() => {
        handleBookingTypeChange(serv, true);
      }, 100);
    } else {
      dispatch({ type: "setService", payload: null });
      dispatch({ type: "setCategory", payload: null });
      dispatch({ type: "setPickupDate", payload: null });
      dispatch({ type: "setDeliveryDate", payload: null });
      dispatch({ type: "setAddresses", payload: null });
      setReload(Date.now().toString());
    }
  };
  const getLaundryItem = (id) => {
    return laundryItems.find((x) => x.id == id);
  };
  const handleBookingTypeChange = (id, clearTemp) => {
    if (count != 0 && !clearTemp) {
      setTempBook(id);
    } else {
      setTempBook(null);
      dispatch({
        type: "setBookingType",
        payload: id,
      });
    }
  };
  const getOffer = (id) => {
    return getLaundryItem(id)?.bundle_offer;
  };

  const filteredItems = formik.values.cart.items
    .map((item, originalIndex) => ({ item, originalIndex }))
    .filter(({ item }) => {
      if (!search) return true;

      const li = getLaundryItem(item.laundry_item_id);
      const name = (li?.name || "").toLowerCase();
      const desc = (li?.description || "").toLowerCase();
      const q = search.toLowerCase();

      return name.includes(q) || desc.includes(q);
    });

  // ==================== SUMMARY (Step 2) ====================
  const setNote = (e) => {
    dispatch({ type: "setNote", payload: e });
  };

  const HandlePickupChange = (e) => {
    setSPickup(e.date);
    setSPSlot(e.slot);
    dispatch({ type: "setPickupDate", payload: e });
    setShowPickup(false);
  };
  const HandleDeliveryChange = useCallback(
    (e) => {
      setSDelivery(e.date);
      setSDSlot(e.slot);
      dispatch({ type: "setDeliveryDate", payload: e });
      setShowDelivery(false);
    },
    [dispatch]
  );
  const checkCoupon = () => {
    if (loggedIn) {
      if (coupon) {
        dispatch({ type: "setCoupon", payload: null });
        setCode("");
      } else if (code.trim() !== "" && !cLoading) {
        setCLoading(true);
        fetchCoupon({
          code,
          amount: grossTotal,
          zone_id: zone,
        }).then(
          (res) => {
            setCLoading(false);
            dispatch({ type: "setCoupon", payload: res });
          },
          (err) => {
            setCLoading(false);
            err?.errors?.forEach((er) => {
              toast.error(er.message);
            });
          }
        );
      }
    } else setShowWarning(true);
  };
  const HandleAddressChange = (e) => {
    if (e.laundry_zone_id == zone) {
      if (addressModal === "pickup") {
        setAddress((v) => ({ ...v, pickup: e }));
      } else {
        setAddress((v) => ({ ...v, delivery: e }));
      }
      setAddressModal(null);
    } else {
      setTAddress(e);
    }
  };
  const ReplaceAddress = (e) => {
    if (addressModal === "pickup") {
      setAddress((v) => ({ pickup: e, delivery: null }));
    } else {
      setAddress((v) => ({ pickup: null, delivery: e }));
    }
    setAddressModal(null);
    setTAddress(null);

    dispatch({ type: "setZone", payload: e.laundry_zone_id });
    dispatch({ type: "setCart", payload: [] });
    dispatch({ type: "setOrderType", payload: null });
    dispatch({ type: "setBookingType", payload: null });
    dispatch({ type: "setOrderTypeId", payload: null });

    dispatch({ type: "setService", payload: null });
    dispatch({ type: "setCategory", payload: null });
  };
  const FilterRange = useCallback(
    (range) => {
      if (!weekDaysOff) return range;
      return range.filter((date) => {
        const day = format(date, "EEEE").toLowerCase();
        const dateStringFormatted = format(date, "yyyy-MM-dd");

        return (
          !weekDaysOff?.weekdaysOff.includes(day) &&
          !weekDaysOff?.offDays.includes(dateStringFormatted)
        );
      });
    },
    [weekDaysOff]
  );

  React.useEffect(() => {
    if (deliveryType && step >= 2)
      fetchWeekDaysOff(deliveryType?.id).then(
        (res) => setWeekDaysOff(res),
        () => toast.error("Something went wrong!")
      );
  }, [deliveryType, step]);

  React.useEffect(() => {
    if (weekDaysOff && deliveryType && step >= 2) {
      setPickupLoading(true);
      var sd = addDays(new Date(), deliveryType?.pickup_duration);
      const ed = addDays(sd, (deliveryType?.noOf_calender_dates || 15) - 1);
      const dif = FilterRange(eachDayOfInterval({ start: sd, end: ed }));
      if (pickupDate?.date && pickupDate?.slot) {
        fetchTimeSlot(
          format(pickupDate?.date, "yyyy-MM-dd"),
          1,
          bookingType
        ).then(
          (res) => {
            const sl = res.findIndex((x) => x.id === pickupDate.slot.id);
            if (~sl) {
              setSPickup(pickupDate?.date);
              setSPSlot(pickupDate?.slot);
            } else {
              dispatch({ type: "setPickupDate", payload: null });
              toast.warn(
                "Pickup Time slot you selected last time is not available now please select another slot!"
              );
            }
            setPickupLoading(false);
          },
          (er) => {
            toast.error("Something went wrong while fetching Time Slots.");
            setPickupLoading(false);
          }
        );
      } else setPickupLoading(false);
      setPickupRange(dif);
    }
  }, [
    pickupDate,
    weekDaysOff,
    deliveryType,
    bookingType,
    FilterRange,
    dispatch,
    step,
  ]);

  React.useEffect(() => {
    if (weekDaysOff && deliveryType && step >= 2) {
      if (pickupDate) {
        let skip = deliveryType?.duration;
        if (orderTypeId != 1 && orderTypeId != 2) {
          const serv = services.find((x) => x.id == items[0]?.services_id);
          if (serv)
            skip =
              bookingType == 1 ? serv.regular_duration : serv.express_duration;
        }

        const sd = addDays(pickupDate.date, skip);
        const ed = addDays(sd, (deliveryType?.noOf_calender_dates || 15) - 1);
        const dif = FilterRange(eachDayOfInterval({ start: sd, end: ed }));
        setDeliveryRange(dif);
        if (deliveryDate?.date) {
          const ld =
            typeof deliveryDate.date == "string"
              ? parseISO(deliveryDate.date)
              : deliveryDate.date;
          if (!isDateBetween(ld, sd, ed)) {
            dispatch({ type: "setDeliveryDate", payload: null });
          } else if (deliveryDate.slot) {
            setDeliveryLoading(true);
            fetchTimeSlot(
              format(ld, "yyyy-MM-dd"),
              2,
              bookingType,
              pickupDate.slot?.id,
              format(pickupDate.date, "yyyy-MM-dd")
            ).then(
              (res) => {
                const sl = res.findIndex((x) => x.id === deliveryDate?.slot.id);
                if (~sl) {
                  setSDelivery(ld);
                  setSDSlot(deliveryDate?.slot);
                } else {
                  dispatch({ type: "setDeliveryDate", payload: null });
                  toast.warn(
                    "Delivery Time slot you selected last time is not available now please select another slot!"
                  );
                }
                setDeliveryLoading(false);
              },
              (er) => {
                toast.error("Something went wrong while fetching Time Slots.");
                setDeliveryLoading(false);
              }
            );
          }
        } else {
          if (!deliveryDate || !deliveryDate?.slot) {
            const pr = async () => {
              setDeliveryLoading(true);
              for (let i = 0; i < dif.length; i++) {
                const d = dif[i];
                try {
                  const res = await fetchTimeSlot(
                    format(deliveryDate?.date || d, "yyyy-MM-dd"),
                    2,
                    bookingType,
                    pickupDate.slot?.id,
                    format(pickupDate.date, "yyyy-MM-dd")
                  );
                  if (res.length > 0) {
                    HandleDeliveryChange({
                      date: deliveryDate?.date || d,
                      slot: res[0],
                    });
                    break;
                  }
                } catch (er) {
                  console.warn(er, "error on slot");
                  toast.error(
                    "Something went wrong while fetching Time Slots."
                  );
                }
              }
              setDeliveryLoading(false);
            };
            pr();
          }
        }
      }
    }
  }, [
    pickupDate,
    deliveryType,
    bookingType,
    deliveryDate,
    weekDaysOff,
    orderTypeId,
    items,
    services,
    FilterRange,
    dispatch,
    HandleDeliveryChange,
    step,
  ]);

  React.useEffect(() => {
    if (address.delivery || address.pickup) {
      dispatch({ type: "setAddresses", payload: address });
    }
  }, [address]);
  React.useEffect(() => {
    if (!loaded) {
      if (addresses) {
        setAddress(addresses);
      } else if (selectedAddress?.id || selectedAddress?.address1) {
        setAddress({ pickup: selectedAddress, delivery: selectedAddress });
      }
      setLoaded(true);
    }
  }, [addresses, selectedAddress, loaded]);

  React.useEffect(() => {
    if (services.length !== 0 && cart.length !== 0) {
      const temp = cart.reduce((result, item) => {
        const key = item.serviceId;
        setOpen((prev) => ({
          ...prev,
          [key]: true,
        }));
        if (!result[key]) {
          result[key] = {
            services_id: key,
            service: services.find((s) => s.id === key)?.name,
            open: true,
            items: [],
          };
        }
        const {
          laundry_item_id,
          laundry_item_category_id,
          price,
          quantity,
          bar_code,
          name,
          addons,
          is_bundle_active,
          bundle_free,
          free_items,
          bundle_buy,
          item_note,
        } = item;
        result[key].items.push({
          laundry_item_id,
          laundry_item_category_id: parseInt(laundry_item_category_id),
          price,
          quantity,
          bar_code,
          name,
          addons,
          free_items,
          bundle_free,
          bundle_buy,
          is_bundle_active,
          item_note,
        });
        return result;
      }, {});
      setItems(Object.values(temp));
    }
  }, [cart, services]);

  // ==================== REVIEW (Step 3) ====================
  const retrieveWallet = () => {
    setWalletLoading(true);
    walletRetrieve().then(
      (res) => {
        let balance = 0;
        res.data.forEach((x) => {
          balance += x.balance;
        });
        setWalletBalance(balance);
        setWalletLoading(false);
      },
      (er) => {
        console.warn(er);
        toast.error("Something went wrong while fetching wallet details.");
        setWalletLoading(false);
      }
    );
  };
  React.useEffect(() => {
    if (step === 3) retrieveWallet();
  }, [step]);

  const referred_by = getItem("referred_by");
  let formattedNote = null;
  if (Array.isArray(note) && note.length > 0) {
    formattedNote = note;
  }

  const handlePlaceOrder = () => {
    if (!submitting && items.length !== 0) {
      const maxOrders = config?.laundry_customer_maximum_order;
      if (ongoingOrders?.length < maxOrders) {
        setSubmitting(true);
        placeOrder();
      } else
        toast.error(
          `You already have ${ongoingOrders?.length} in queue please try again later.`
        );
    }
  };

  const placeOrder = () => {
    const payload = {
      payment_status: "unpaid",
      delivery_date: format(deliveryDate?.date, "yyyy-MM-dd HH:mm:ss.SSS"),
      pickup_date: format(pickupDate?.date, "yyyy-MM-dd HH:mm:ss.SSS"),
      payment_method: paymentMethod,
      platform: 1,
      referred_by: referred_by,
      consumer_referral_code: consumerRefCode,
      referral_discount: referralDiscount,
      pickup_coordinates: {
        latitude: address?.pickup?.latitude,
        longitude: address?.pickup?.longitude,
      },
      destination_coordinates: {
        latitude: address?.delivery?.latitude,
        longitude: address?.delivery?.longitude,
      },
      pickup_address: {
        floor: null,
        road: address?.pickup?.streetNo,
        house: address?.pickup?.houseNo,
        address: address?.pickup?.address,
        contact_person_name: address?.pickup?.contact_person_name,
        contact_person_number: address?.pickup?.contact_person_number,
        addressLine1: address?.pickup?.address1 || "",
        addressLine2: address?.pickup?.address2 || "",
        area: address?.pickup?.area?.[0]?.name || "",
        area_phase: address?.pickup?.phase?.[0]?.name || "",
      },
      destination_address: {
        floor: null,
        road: address?.delivery?.streetNo,
        house: address?.delivery?.houseNo,
        address: address?.delivery?.address,
        contact_person_name: address?.delivery?.contact_person_name,
        contact_person_number: address?.delivery?.contact_person_number,
        addressLine1: address?.delivery?.address1 || "",
        addressLine2: address?.delivery?.address2 || "",
        area: address?.delivery?.area?.[0]?.name || "",
        area_phase: address?.delivery?.phase?.[0]?.name || "",
      },
      bar_code: "",
      order_pickup_time_slot_id: pickupDate?.slot?.id,
      order_type_id: deliveryType?.id,
      order_delivery_time_slot_id: deliveryDate?.slot?.id,
      laundry_delivery_type_id: deliveryType?.id,
      service_id: 19,
      category_id: 1,
      delivery_charge: deliveryType?.charge,
      pickup_schedule_at: format(pickupDate?.date, "yyyy-MM-dd HH:mm:ss.SSS"),
      delivery_schedule_at: format(
        deliveryDate?.date,
        "yyyy-MM-dd HH:mm:ss.SSS"
      ),
      pending: "",
      distance: 0.0,
      note: formattedNote,
      order_pickup_time_slot_date: format(
        pickupDate?.date,
        "yyyy-MM-dd HH:mm:ss.SSS"
      ),
      order_delivery_time_slot_date: format(
        deliveryDate?.date,
        "yyyy-MM-dd HH:mm:ss.SSS"
      ),
      laundryZoneId: zone,
      coupon_code: coupon?.coupon?.code,
      order_pickup_time_slot: `${pickupDate?.slot?.start_time} - ${pickupDate?.slot?.end_time}`,
      order_delivery_time_slot: `${deliveryDate?.slot?.start_time} - ${deliveryDate?.slot?.end_time}`,
      cart: items,
      tax_amount: tax,
      order_amount: total,
      booking_type: bookingType,
    };
    const afterOrder = (order) => {
      setSubmitting(false);
      console.log(order);
      if (paymentMethod !== "digital_payment") {
        const purchaseCurrency = "PKR";
        if (typeof window.fbq !== "undefined") {
          const values = {
            value: total,
            currency: purchaseCurrency,
            order_id: order?.order_id,
            quantity: count,
          };
          window.fbq("track", "Purchase", values);
        }
        navigate(`/app/order-placed/${order?.order_id}`, {
          replace: true,
        });
      }
      dispatch({ type: "setCart", payload: [] });
      dispatch({ type: "setOrderType", payload: null });
      dispatch({ type: "setBookingType", payload: null });
      dispatch({ type: "setOrderTypeId", payload: null });

      dispatch({ type: "setService", payload: null });
      dispatch({ type: "setCategory", payload: null });
      dispatch({ type: "setDeliveryDate", payload: null });
      dispatch({ type: "setPickupDate", payload: null });
      dispatch({ type: "setCoupon", payload: null });
      dispatch({ type: "setAddresses", payload: null });
      dispatch({ type: "setNote", payload: null });
    };
    createOrder(payload).then(
      (res) => {
        if (paymentMethod === "digital_payment") {
          window.location.replace(
            `${baseUrl}/payfast-payment?group_id=${res?.order_id}`
          );
        }
        afterOrder(res);
      },
      (er) => {
        setSubmitting(false);
        if (er && er.errors && er.errors.length !== 0) {
          console.log(er);
          er.errors.forEach((x) => {
            toast.error(x.message);
          });
        }
      }
    );
  };

  // ==================== STEP LOCK HELPERS ====================
  const isStepComplete = (s) => {
    if (s === 1) return cart.length > 0;
    if (s === 2)
      return !!(
        pickupDate?.date &&
        pickupDate?.slot &&
        deliveryDate?.date &&
        deliveryDate?.slot &&
        address?.pickup &&
        address?.delivery
      );
    return false;
  };

  const stepsRef = React.useRef(null);
  const scrollToSteps = () => {
    setTimeout(
      () => stepsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }),
      50
    );
  };

  const proceedToSummary = () => {
    if (count === 0) {
      toast.error("Please add items to your cart first.");
      return;
    }
    if (config?.laundry_customer_minimum_purchase <= amount) {
      scrollToSteps();
      setStep(2);
    } else {
      toast.error(
        `Minimum order value should be above Rs.${config?.laundry_customer_minimum_purchase}`
      );
    }
  };

  const proceedToReview = () => {
    if (!deliveryDate || !pickupDate) {
      toast.error("Please select pickup & delivery dates to continue.");
      return;
    }
    if (!address.pickup || !address.delivery) {
      toast.error("Please select pickup & delivery addresses to continue.");
      return;
    }
    scrollToSteps();
    setStep(3);
  };

  // ==================== RENDER ====================
  return (
    <div className="container py-4 consumer-new-order" style={{ minHeight: "80vh" }}>
      {/* Header */}
      <div className="row mb-3">
        <div className="col-12">
          <h3 className="fw-bold mb-0">New Order</h3>
        </div>
      </div>

      {/* Stepper */}
      <div className="row mb-4">
        <div className="col-12">
          <OrderStepper step={step} isStepComplete={isStepComplete} setStep={setStep} />
        </div>
      </div>

      {/* ==================== STEPS CONTENT ==================== */}
      <div className="row" ref={stepsRef}>
        <div className="col-lg-8 consumer-order-content">
          {/* ===== STEP 1: SELECT ITEMS ===== */}
          <StepCard
            title={`1. Select Items ${cart.length > 0 ? `(${count} items)` : ""}`}
            step={1}
            currentStep={step}
            completed={isStepComplete(1)}
            onEdit={() => setStep(1)}
            wide
          >
            <ItemsStep
              initLoading={initLoading}
              orderTypeId={orderTypeId}
              bookingType={bookingType}
              handleBookingTypeChange={handleBookingTypeChange}
              payload={payload}
              selectService={selectService}
              services={services}
              categories={categories}
              selectCategory={selectCategory}
              search={search}
              setSearch={setSearch}
              itemsLoading={itemsLoading}
              fetchError={fetchError}
              setReload={setReload}
              filteredItems={filteredItems}
              getLaundryItem={getLaundryItem}
              campaign={campaign}
              changeAddonQuantity={changeAddonQuantity}
              changeItemQuantity={changeItemQuantity}
              toggleAddons={toggleAddons}
              getOffer={getOffer}
              changeItemNote={changeItemNote}
              count={count}
              amount={amount}
              setCShow={setCShow}
            />
          </StepCard>

          {/* ===== STEP 2: SUMMARY ===== */}
          <StepCard
            title="2. Summary"
            step={2}
            currentStep={step}
            completed={isStepComplete(2)}
            onEdit={() => setStep(2)}
          >
            <SummaryStep
              items={items}
              open={open}
              setOpen={setOpen}
              deliveryType={deliveryType}
              code={code}
              setCode={setCode}
              checkCoupon={checkCoupon}
              cLoading={cLoading}
              coupon={coupon}
              address={address}
              setAddressModal={setAddressModal}
              setShowWarning={setShowWarning}
              loggedIn={loggedIn}
              sPickup={sPickup}
              sPSlot={sPSlot}
              setShowPickup={setShowPickup}
              sDelivery={sDelivery}
              sDSlot={sDSlot}
              setShowDelivery={setShowDelivery}
              pickupLoading={pickupLoading}
              deliveryLoading={deliveryLoading}
              apiNotes={apiNotes}
              note={note}
              setNote={setNote}
            />
          </StepCard>

          {/* ===== STEP 3: REVIEW & PLACE ===== */}
          <StepCard
            title="3. Review & Place Order"
            step={3}
            currentStep={step}
            completed={false}
            onEdit={() => setStep(3)}
          >
            <ReviewStep
              items={items}
              open={open}
              setOpen={setOpen}
              deliveryType={deliveryType}
              pickupDate={pickupDate}
              deliveryDate={deliveryDate}
              paymentMethod={paymentMethod}
              setPaymentMethod={setPaymentMethod}
              total={total}
              walletBalance={walletBalance}
              walletLoading={walletLoading}
              address={address}
              note={note}
            />
          </StepCard>
        </div>

        {/* ===== RIGHT SIDEBAR: Cart Preview ===== */}
        <div className="col-lg-4 consumer-order-sidebar">
          <CartSidebar
            cart={cart}
            count={count}
            items={items}
            open={open}
            setOpen={setOpen}
            amount={amount}
            referralDiscount={referralDiscount}
            subtotal={subtotal}
            discount={discount}
            coupon={coupon}
            tax={tax}
            deliveryCharge={deliveryCharge}
            total={total}
            step={step}
            setStep={setStep}
            proceedToSummary={proceedToSummary}
            proceedToReview={proceedToReview}
            handlePlaceOrder={handlePlaceOrder}
            submitting={submitting}
          />
        </div>
      </div>

      {/* Modals */}
      <OrderModals
        cShow={cShow}
        setCShow={setCShow}
        cCart={cCart}
        tempServ={tempServ}
        tempBook={tempBook}
        count={count}
        setTempServ={setTempServ}
        setTempBook={setTempBook}
        showPickup={showPickup}
        sPickup={sPickup}
        sPSlot={sPSlot}
        pickupRange={pickupRange}
        bookingType={bookingType}
        HandlePickupChange={HandlePickupChange}
        setShowPickup={setShowPickup}
        showDelivery={showDelivery}
        sDelivery={sDelivery}
        sDSlot={sDSlot}
        deliveryRange={deliveryRange}
        HandleDeliveryChange={HandleDeliveryChange}
        setShowDelivery={setShowDelivery}
        addressModal={addressModal}
        setAddressModal={setAddressModal}
        HandleAddressChange={HandleAddressChange}
        tAddress={tAddress}
        setTAddress={setTAddress}
        ReplaceAddress={ReplaceAddress}
        loggedIn={loggedIn}
        showWarning={showWarning}
        setShowWarning={setShowWarning}
      />
    </div>
  );
};

export default ConsumerNewOrder;
